import findResult, {
  assignValue,
  findWithCodes, hasKey, hasMinimumLength,
  isEmpty,
  selectableResult,
} from '@/utils/resultsHelper';
import pagingWithFilteredResults, { pagingWithResults, presentationOptions, sortingFilters } from '@/constants/states';
import defaultStates, { readStates, saveStates, states } from '@/constants/lifecycle';
import { alignFieldsToForm, crushFormProps } from '@/forms/state/state';
import { pruneEntityFieldErrors } from '@/utils/errors';
import fieldNames from '@/constants/fields';
import initialTemplatesState from '../templates/state/state';
import {
  requestTaskLists,
  requestTaskPermissions,
  requestTaskStates,
  requestTaskTabs,
} from '../constants';

export const requestTaskPerms = (can = false) => {
  const permissions = {};
  Object.values(requestTaskPermissions).forEach((permission) => {
    permissions[permission] = can;
  });
  return permissions;
};

export const activeRequestTask = (open = false) => ({
  activeStepIndex: 0,
  review_approved: false,
  assignee: null,
  code: null,
  isDirty: false,
  isEditing: false,
  fields: {},
  formKey: '',
  groups: [],
  name: '',
  open,
  permissions: requestTaskPerms(false),
  ...requestTaskStates,
});

export const manageRequestTask = (open = false) => ({
  open,
  code: null,
  name: '',
  isDirty: false,
  groups: [],
  assignee: { email: null },
  ...readStates,
  ...saveStates,
});

export const allRequestTasks = (
  view = null,
  orderBy = [{ column: 'name', direction: 'asc' }],
  singleSorting = true,
  display = presentationOptions.TABLE,
) => ({
  all: {
    ...pagingWithFilteredResults(),
    ...defaultStates,
    ...sortingFilters(orderBy, singleSorting),
    view,
    display,
  },
});

export const requestTaskViews = () => ({
  ...pagingWithResults(10000),
  ...readStates,
});

export const tasksResult = (result) => {
  const item = { ...selectableResult(result), ...requestTaskStates };
  if (isEmpty(item.request)) { item.request = { name: '', datasets: [], workflows: [] }; }
  if (isEmpty(item.workflow)) { item.workflow = { name: '' }; }
  if (isEmpty(item.request.requester)) { item.request.requester = { name: '' }; }
  if (item.request.datasets?.length === 0) { item.request.datasets = [{ catalogue: { title: '' } }]; }

  return item;
};

export const taskRequestWarning = ({ request: { code, datasets } }) => (
  !hasMinimumLength(code) || !hasMinimumLength(datasets[0].catalogue.title)
);

export const taskRequestAssignedToMe = (email, { assignee }) => (
  hasMinimumLength(assignee?.email) && email === assignee.email
);

export const findRequestTask = ({ tasks }, { category, code }) => findResult(tasks, category, code);

export const findAllRequestTasks = ({ tasks }, { items }) => {
  const codes = items.map(({ code }) => code);
  return findWithCodes(tasks, requestTaskLists, codes);
};

export const findRequestTasks = ({ tasks }, { category }) => tasks[category];

export const findRequestTaskViews = ({ tasks: { views } }, { category }) => views[category];

const initialState = {
  tasks: {
    activeTask: activeRequestTask(),
    manageTask: manageRequestTask(),
    ...allRequestTasks(),
    views: { all: requestTaskViews() },
    ...initialTemplatesState,
  },
};

export const requestTaskStateToLoad = (tasks) => {
  const allFilters = tasks.all || allRequestTasks().all;

  return {
    tasks: {
      ...initialState.tasks,
      ...allRequestTasks(
        allFilters.view,
        allFilters.orderBy,
        allFilters.singleSorting,
        allFilters.display,
      ),
    },
  };
};

export const requestTasksStateToSave = ({ tasks }) => (
  {
    tasks: {
      all: {
        view: tasks.all.view,
        display: tasks.all.display,
        orderBy: tasks.all.orderBy,
        singleSorting: tasks.all.singleSorting,
      },
    },
  }
);

export const requestTaskStepNames = (steps) => Object.values(steps).map(({ name }) => name);

const allowedTabNames = (permissions, formKey, fields, sections) => {
  const names = requestTaskStepNames(requestTaskTabs);

  if (!hasKey(sections, 'comments')) {
    names.splice(names.indexOf(requestTaskTabs.COMMENTS.name), 1);
  }

  if (!hasMinimumLength(formKey) || (!permissions[requestTaskPermissions.UPDATE]
    && Object.values(fields).length === 0)) {
    names.splice(names.indexOf(requestTaskTabs.TASK.name), 1);
  }

  return names;
};

export const allowedRequestTaskTabIndexes = ({ permissions, formKey, fields }, { sections }) => {
  const names = allowedTabNames(permissions, formKey, fields, sections);
  return names.map((name) => requestTaskStepNames(requestTaskTabs).indexOf(name));
};

export const claimStateWithPermissions = ({ assignee, permissions }, canEdit = true, canUnClaim = true) => {
  const state = { claimed: !isEmpty(assignee), canClaim: false };
  state.canEdit = canEdit && permissions[requestTaskPermissions.UPDATE];
  state.canClaim = state.claimed
    ? canUnClaim && permissions[requestTaskPermissions.UNCLAIM]
    : canEdit && permissions[requestTaskPermissions.CLAIM];

  return state;
};

export const claimState = (
  { assignee, permissions },
  { permissions: { tasks: { update: canEdit, unclaim: canUnClaim } } },
) => (
  { ...claimStateWithPermissions({ assignee, permissions }, canEdit, canUnClaim) }
);

export const assignRequestTaskPropsNoSplit = (task) => {
  const firstRun = !task.isEditing;
  if (!task.saveErrors) { assignValue(task, 'saveErrors', []); }
  if (!task.saveStatus) { assignValue(task, 'saveStatus', states.NONE); }

  assignValue(task, 'isEditing', true);
  return firstRun;
};

const crushRequestTaskProps = (task, template) => {
  const copy = crushFormProps(task.fields);
  const { errors, redundant } = alignFieldsToForm(copy, template);
  redundant.forEach((key) => delete copy[key]);
  assignValue(task, 'fields', copy);

  return errors.length > 0;
};

export const assignRequestTaskProps = ({ fields, saveErrors }, template) => {
  const { errors } = alignFieldsToForm(fields, template);
  errors.forEach((e) => { saveErrors.push(e); });

  return errors.length > 0;
};

export const assignTaskEditProps = (task, template, crush = false) => {
  const requiresSplit = template.code && assignRequestTaskPropsNoSplit(task);
  if (requiresSplit) {
    return crush ? crushRequestTaskProps(task, template) : assignRequestTaskProps(task, template);
  }

  return false;
};

export const pruneManageTaskErrors = (errors, { properties }) => {
  let filteredErrors = errors;
  if (hasKey(properties, 'assignee')) {
    filteredErrors = pruneEntityFieldErrors(filteredErrors, fieldNames.EMAIL, ['task']);
  }

  return filteredErrors;
};

export default initialState;
